// @flow
import BluebirdPromise from 'bluebird';
import chunk from 'lodash/fp/chunk';

import * as r from './resources';
import {
  jsonForResourceRequest,
  rangeString,
  get,
  fullResultsOfPagedQuery,
  chunkedFullResultsOfPagedQuery,
} from './http';

import type { Dispatch, GetState } from '~/store';
import type {
  Product,
  ProductsQuery,
  ProductsQueryResponse,
  VariationsQueryResponse,
  ObjectMovieQuery,
  ObjectMovieQueryResponse,
} from 'modules/product';

export const PRODUCTS_QUERY_DEFAULT_PAGE_SIZE: 20 = 20;

export const productsQuery =
  (
    apiUrl: string,
    query: ProductsQuery,
  ): ((
    start: number,
    count: number,
  ) => BluebirdPromise<ProductsQueryResponse>) =>
  (start: number, count: number): BluebirdPromise<ProductsQueryResponse> =>
    jsonForResourceRequest({
      apiUrl,
      resourceId: ({
        ...query,
        Range: rangeString({ start, count }),
      }: any),
      resource: r.products,
    });

export const variationsQuery =
  (
    apiUrl: string,
    query: ProductsQuery,
  ): ((
    start: number,
    count: number,
  ) => BluebirdPromise<VariationsQueryResponse>) =>
  (start: number, count: number): BluebirdPromise<VariationsQueryResponse> =>
    jsonForResourceRequest({
      apiUrl,
      resourceId: ({
        ...query,
        variations: true,
        tag_facets: true,
        Range: rangeString({ start, count }),
      }: any),
      resource: r.variations,
    });

export const externalPrices = ({
  catalogKey,
  orderType,
  customerNumber,
  products = [],
  variationIds = [],
  stockItemIds = [],
}: {
  catalogKey: string,
  orderType: string | null,
  customerNumber: string,
  products: Product[],
  variationIds?: string[],
  stockItemIds?: string[],
}): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  // this is a dirty hack because Resource expects a string here
  get(
    r.externalPrices,
    ({
      catalogKey,
      orderType,
      customerNumber,
      products,
      variationIds,
      stockItemIds,
    }: any),
  );

export const getVariationPairObjectMovies = (
  apiUrl: string,
  query: ObjectMovieQuery,
): BluebirdPromise<ObjectMovieQueryResponse> =>
  jsonForResourceRequest({
    apiUrl,
    resourceId: (query: any),
    resource: r.variationPairObjectMovie,
  });

export const allProductsForQuery =
  (
    apiUrl: string,
  ): ((
    query: ProductsQuery,
    onProgress: (
      total: number,
      completed: number,
      response: ProductsQueryResponse,
    ) => void,
  ) => BluebirdPromise<$ReadOnlyArray<Product>>) =>
  (
    query: ProductsQuery,
    onProgress: (
      total: number,
      completed: number,
      response: ProductsQueryResponse,
    ) => void,
  ): BluebirdPromise<$ReadOnlyArray<Product>> =>
    fullResultsOfPagedQuery(
      productsQuery(apiUrl, query),
      PRODUCTS_QUERY_DEFAULT_PAGE_SIZE,
      onProgress,
    );

export const allProductsForStockItemKeyListQuery =
  (
    apiUrl: string,
  ): ((
    query: ProductsQuery,
    stockItemKeys: $ReadOnlyArray<string>,
    onProgress?: (
      total: number,
      completed: number,
      response: ProductsQueryResponse,
    ) => void,
  ) => BluebirdPromise<$ReadOnlyArray<Product>>) =>
  (
    query: ProductsQuery,
    stockItemKeys: $ReadOnlyArray<string>,
    onProgress?: (
      total: number,
      completed: number,
      response: ProductsQueryResponse,
    ) => void,
  ): BluebirdPromise<$ReadOnlyArray<Product>> => {
    const chunks = chunk(250, stockItemKeys);

    const fetcherBuilder = (keys: $ReadOnlyArray<string>) =>
      productsQuery(apiUrl, {
        ...query,
        keys,
      });

    return chunkedFullResultsOfPagedQuery(
      fetcherBuilder,
      chunks,
      PRODUCTS_QUERY_DEFAULT_PAGE_SIZE,
      onProgress,
    );
  };
